// File: `.../GroupExcludeDiscussionDialog.tsx`
import React, { useMemo, useState } from "react";
import logger from "@/lib/logger";
import { Localizer } from "@/main/react/Types";
import { AjaxGroupRestControllerApi } from "@/generated-client";
import { ClientFactory } from "@/main/common/ClientFactory";
import { AlertTriangle, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { ErrorBanner } from "@/main/react/component/ErrorBanner";
import SimpleDialog, { openSimpleDialog, SimpleDialogFooter } from "@/main/react/Dialog/SimpleDialog";
import { REQUEST_VALIDATION_LIMITS } from "@/main/validation/request";

type InnerProps = {
    groupPublicId: string;
    discussionPublicId: string;
    onClose?: () => void;
    localizer: Localizer;
    onSuccess?: () => void;
};

export function GroupExcludeDiscussionDialogInner({
                                                     groupPublicId,
                                                     discussionPublicId,
                                                     onClose,
                                                     localizer,
                                                     onSuccess,
                                                 }: InnerProps) {
    const [saving, setSaving] = useState(false);
    const [reason, setReason] = useState("");
    const [error, setError] = useState<string | null>(null);

    const apiClient: AjaxGroupRestControllerApi = useMemo(() => ClientFactory.getAjaxGroupRestControllerApi(), []);

    async function handleSubmit(e?: React.FormEvent) {
        e?.preventDefault();
        setError(null);

        const trimmedReason = reason.trim();

        if (!groupPublicId || !discussionPublicId) {
            setError(localizer("groups.modal.exclude.invalid_discussion_id"));
            return;
        }
        if (!trimmedReason) {
            setError(localizer("groups.modal.exclude.require_reason"));
            return;
        }
        if (trimmedReason.length > REQUEST_VALIDATION_LIMITS.longText.maxLength) {
            setError(`Reason must be at most ${REQUEST_VALIDATION_LIMITS.longText.maxLength} characters.`);
            return;
        }

        setSaving(true);
        try {
            await apiClient.excludeDiscussion(groupPublicId, discussionPublicId, { reason: trimmedReason });

            onSuccess?.();
            onClose?.();
        } catch (err) {
            // Log and surface a user-friendly error
            logger.error("Failed excluding discussion", { err });
            setError(localizer("groups.modal.exclude.failed"));
        } finally {
            setSaving(false);
        }
    }

    return (
        <form onSubmit={handleSubmit} className="w-full">
            <div className="flex flex-col gap-4">
                {error ? <ErrorBanner description={error} className="rounded-xl py-3" /> : null}

                <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium">
                        {localizer("groups.modal.exclude.reason_label")}
                    </label>
                    <Textarea
                        value={reason}
                        onChange={(e: any) => setReason(e.target.value)}
                        placeholder={localizer("groups.modal.exclude.reason_placeholder")}
                        rows={5}
                        maxLength={REQUEST_VALIDATION_LIMITS.longText.maxLength}
                        disabled={saving}
                    />
                </div>
            </div>

            <SimpleDialogFooter>
                <CtaButton
                    type="button"
                    onClick={() => onClose?.()}
                    text={localizer("literal.cancel")}
                    extraClass=""
                    icon={X}
                    disabled={saving}
                    variant={"outline"}
                />

                <CtaButton
                    type="button"
                    onClick={() => handleSubmit()}
                    text={localizer("groups.modal.exclude.exclude_button")}
                    extraClass=""
                    icon={AlertTriangle}
                    disabled={saving}
                    variant={"destructive"}
                />
            </SimpleDialogFooter>
        </form>
    );
}

export function showExcludeDiscussionDialog(
    groupPublicId: string,
    discussionPublicId: string,
    localizer: Localizer,
    onSuccess?: () => void
) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={<div id="exclude-discussion-modal-title">{localizer("groups.modal.exclude_discussion.title")}</div>}
            width="md"
        >
            <GroupExcludeDiscussionDialogInner
                groupPublicId={groupPublicId}
                discussionPublicId={discussionPublicId}
                onClose={() => onOpenChange(false)}
                localizer={localizer}
                onSuccess={onSuccess}
            />
        </SimpleDialog>
    ));
}
